// Reçus fiscaux (D-124) : les montants, les états du registre, les textes. Rien ici n'écrit ni ne lit la base.

export type ReceiptStatus = 'a_signer' | 'signe' | 'envoye' | 'annule';
export type ReceiptKind = 'frais' | 'don';
export type GiftMode = 'especes' | 'cheque' | 'virement' | 'autre';
export type GiftForm = 'acte_authentique' | 'seing_prive' | 'don_manuel' | 'autres';

export interface Donor {
  kind: 'person' | 'company';
  last_name?: string; first_names?: string;
  company?: string; legal_form?: string; siren?: string;
  num?: string | null; street?: string; zip?: string; city?: string; country?: string; email?: string;
}
export interface Org {
  name: string; ident: string | null; num: string | null; street: string | null; zip: string | null; city: string | null; country: string | null;
  purpose: string | null; form_cash: GiftForm | null; form_expenses: GiftForm | null;
}

/** Réduction d'impôt des particuliers (article 200 du CGI) : 66 % du don, dans la limite de 20 % du revenu imposable. */
export const REDUCTION_RATE = 0.66;
export const reduction = (cents: number) => Math.round(cents * REDUCTION_RATE);
/** Le montant au barème : km × €/km, arrondi au centime. */
export const amountFor = (km: number, rate: number) => Math.round(km * rate * 100);

const group = (s: string, sep: string) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
/** « 2 143,79 € » (espace fine insécable à l'écran, espace simple sur un PDF). */
export function eur(cents: number, sep = ' '): string {
  const neg = cents < 0, v = Math.abs(Math.round(cents));
  return `${neg ? '−' : ''}${group(String(Math.floor(v / 100)), sep)},${String(v % 100).padStart(2, '0')}${sep === ' ' ? ' ' : ' '}€`;
}
/** « 143,79 » : le montant sans le symbole (case « Euros » du cerfa). */
export const amountDigits = (cents: number) => `${group(String(Math.floor(cents / 100)), ' ')},${String(cents % 100).padStart(2, '0')}`;
/** « 412 km », « 52,5 km » */
export const kmLabel = (km: number) => `${String(Math.round(km * 10) / 10).replace('.', ',')} km`;
/** « 0,349 €/km » */
export const rateLabel = (rate: number) => `${rate.toFixed(3).replace('.', ',')} €/km`;

// --- D-152 : le barème kilométrique des salariés, selon le véhicule (CGI art. 200 et art. 83, 3° ; annexe IV art. 6 B).
// Le barème vit en base (km_scale, avec sa source) et la base fait le calcul ; ici, seulement les libellés.
export type VehicleKind = 'auto' | 'moto' | 'cyclo';
export interface Vehicle { vehicle: VehicleKind; cv: number | null; electric: boolean }
/** Les puissances fiscales proposées, telles que le barème les découpe (la valeur gardée, son libellé). */
export const VEHICLE_POWERS: Record<'auto' | 'moto', { cv: number; label: string }[]> = {
  auto: [{ cv: 3, label: '3 CV ou moins' }, { cv: 4, label: '4 CV' }, { cv: 5, label: '5 CV' }, { cv: 6, label: '6 CV' }, { cv: 7, label: '7 CV ou plus' }],
  moto: [{ cv: 2, label: '1 ou 2 CV' }, { cv: 5, label: '3 à 5 CV' }, { cv: 6, label: 'plus de 5 CV' }],
};
export const VEHICLE_KINDS: { kind: VehicleKind; label: string }[] = [
  { kind: 'auto', label: 'Voiture' }, { kind: 'moto', label: 'Moto ou scooter de plus de 50 cm³' }, { kind: 'cyclo', label: 'Cyclomoteur ou scooter de 50 cm³ ou moins' },
];
/** La puissance proposée qui contient `cv` (ex. 9 CV → « 7 CV ou plus »). */
export function powerOf(kind: 'auto' | 'moto', cv: number | null): number {
  const list = VEHICLE_POWERS[kind];
  if (cv == null) return list[0]!.cv;
  return (list.find(p => cv <= p.cv) ?? list[list.length - 1]!).cv;
}
/** « voiture de 5 CV », « voiture électrique de 3 CV ou moins », « moto de 3 à 5 CV », « cyclomoteur » : comme la base (_km_vehicle_label). */
export function vehicleLabel(v: Vehicle): string {
  const el = v.electric ? ' électrique' : '';
  if (v.vehicle === 'cyclo') return `cyclomoteur${el}`;
  const cv = v.cv ?? 0;
  if (v.vehicle === 'auto') return `voiture${el} de ${cv <= 3 ? '3 CV ou moins' : cv >= 7 ? '7 CV ou plus' : `${cv} CV`}`;
  return `moto${el} de ${cv <= 2 ? '1 ou 2 CV' : cv <= 5 ? '3 à 5 CV' : 'plus de 5 CV'}`;
}
export interface ScaleRow { vehicle: VehicleKind; cv_min: number; cv_max: number; km_over: number; km_upto: number | null; per_km: number; fixed_eur: number }
export interface Scale { year: number; year_from: number; edition: string; source: string; electric_pct: number; rows: ScaleRow[] }
const plain = (n: number) => eur(n * 100).replace(/,00\s*€$/u, '');
/** Les tranches du barème pour ce véhicule : « jusqu’à 5 000 km : 0,636 €/km », « de 5 001 à 20 000 km : 0,357 €/km + 1 395 € »… */
export function scaleLines(scale: Scale, v: Vehicle): string[] {
  const cv = v.vehicle === 'cyclo' ? 0 : v.cv ?? 0;
  return scale.rows.filter(r => r.vehicle === v.vehicle && cv >= r.cv_min && cv <= r.cv_max).sort((a, b) => a.km_over - b.km_over).map(r => {
    const when = r.km_over === 0 ? `jusqu’à ${plain(r.km_upto!)} km` : r.km_upto == null ? `au-delà de ${plain(r.km_over)} km` : `de ${plain(r.km_over + 1)} à ${plain(r.km_upto)} km`;
    return `${when} : ${rateLabel(Number(r.per_km))}${Number(r.fixed_eur) ? ` + ${plain(Number(r.fixed_eur))} €` : ''}`;
  });
}

const pad = (n: number) => String(n).padStart(2, '0');
/** La date du jour au format ISO (fuseau de l'appareil). */
export const isoToday = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** Le jour d'une date (« 2026-10-30 ») ou d'un instant (« 2026-10-29T23:28:00Z » : le jour à l'heure de l'appareil). */
export const dayOf = (iso: string) => (iso.length > 10 ? isoToday(new Date(iso)) : iso);
/** « 30/10/2026 » depuis « 2026-10-30 » (ou un instant). */
export const ddmmyyyy = (iso: string) => { const [y, m, d] = dayOf(iso).split('-'); return `${d}/${m}/${y}`; };
/** « 30/10 » */
export const ddmm = (iso: string) => ddmmyyyy(iso).slice(0, 5);

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
/** « 10 sept. », « 1er oct. » */
export const shortDay = (iso: string) => { const [, m, d] = dayOf(iso).split('-').map(Number); return `${d === 1 ? '1er' : d} ${MONTHS[m! - 1]}`; };
/** « du 10 sept. au 24 oct. » */
export const periodShort = (from: string, to: string) => (from === to ? `le ${shortDay(from)}` : `du ${shortDay(from)} au ${shortDay(to)}`);

/** Un RNA (W + 9 caractères) ou un SIREN (9 chiffres, clé de Luhn). */
export function identKind(raw: string): 'rna' | 'siren' | null {
  const s = raw.replace(/\s/g, '').toUpperCase();
  if (/^W[0-9A-Z]{9}$/.test(s)) return 'rna';
  if (/^[0-9]{9}$/.test(s) && luhn(s)) return 'siren';
  return null;
}
export function luhn(digits: string): boolean {
  let sum = 0;
  [...digits].reverse().forEach((ch, i) => { let d = Number(ch); if (i % 2) { d *= 2; if (d > 9) d -= 9; } sum += d; });
  return sum % 10 === 0;
}
/** « 812 345 678 » */
export const sirenSpaced = (s: string) => s.replace(/\s/g, '').replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');

/** Le donateur en court, pour le registre : « Garage Saint-Jean », « Karim B. ». */
export function donorShort(d: Donor): string {
  if (d.kind === 'company') return d.company ?? '';
  const last = (d.last_name ?? '').trim();
  return `${(d.first_names ?? '').trim().split(/\s+/)[0] ?? ''}${last ? ` ${last.charAt(0).toUpperCase()}.` : ''}`.trim();
}
/** L'adresse en une ligne : « 8 chemin des Vignes, 77999 Rivebelle ». */
export const addressLine = (a: { num?: string | null; street?: string | null; zip?: string | null; city?: string | null }) =>
  [[a.num, a.street].filter(Boolean).join(' '), [a.zip, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

/** Une adresse saisie d'un trait (« 8 chemin des Vignes, 77999 Rivebelle ») en ses morceaux. */
export function parseAddress(line: string): { num: string | null; street: string; zip: string; city: string } | null {
  const m = /^\s*(?:(\d+\s?(?:bis|ter|[a-d])?)[\s,]+)?(.+?)[\s,]+(\d{5})\s+(.+?)\s*$/i.exec(line);
  if (!m) return null;
  return { num: m[1]?.trim() ?? null, street: m[2]!.trim().replace(/,$/, ''), zip: m[3]!, city: m[4]!.trim() };
}

export interface RegRow {
  number: string; kind: ReceiptKind; status: ReceiptStatus; donor: Donor; partner: string | null; amount_cents: number;
  validated_short: string | null; validated_by: string | null; signed_at: string | null; signer_short: string | null; by_hand: boolean;
  cancelled_at: string | null; cancel_reason: string | null; replaced_by: string | null; replaces: string | null; mail: string | null;
}
/** La ligne du registre (maquette reg1) : « Signé le 30/10 par Nathalie D. · envoyé », « À signer · validé par Sophie R. »,
 *  « Annulé le 08/10 : montant erroné, remplacé par le 2026-006 ». */
export function registreLine(r: RegRow): string {
  if (r.status === 'annule') {
    return `Annulé${r.cancelled_at ? ` le ${ddmm(r.cancelled_at)}` : ''}${r.cancel_reason ? ` : ${r.cancel_reason}` : ''}${r.replaced_by ? `, remplacé par le ${r.replaced_by}` : ''}`;
  }
  if (r.status === 'a_signer') return `À signer · validé par ${r.validated_short ?? r.validated_by ?? 'le bureau'}${r.replaces ? ` · remplace le ${r.replaces}` : ''}`;
  const signed = `Signé${r.signed_at ? ` le ${ddmm(r.signed_at)}` : ''}${r.signer_short ? ` par ${r.signer_short}` : ''}${r.by_hand ? ' (à la main)' : ''}`;
  if (r.status === 'envoye') return `${signed} · envoyé`;
  return `${signed} · ${r.mail === 'skipped' ? 'e-mail non parti (adresse d’exemple)' : r.mail === 'failed' ? 'e-mail en échec' : 'à remettre'}`;
}
export const STATUS_LABEL: Record<ReceiptStatus, string> = { a_signer: 'À signer', signe: 'Signé', envoye: 'Envoyé', annule: 'Annulé' };

/** La forme du don à cocher (réglage du club ; vide par défaut : la notice ne tranche pas). */
export const FORM_LABEL: Record<GiftForm, string> = {
  acte_authentique: 'Acte authentique', seing_prive: 'Acte sous seing privé', don_manuel: 'Déclaration de don manuel', autres: 'Autres',
};
export const MODE_LABEL: Record<GiftMode, string> = { especes: 'espèces', cheque: 'chèque', virement: 'virement', autre: 'autre' };

/** La déclaration annuelle des dons : « Exercice 2025 » (clos le 31 décembre) ou « Exercice 2025-2026 ». */
export function exerciseLabel(from: string, to: string): string {
  return from.slice(0, 4) === to.slice(0, 4) ? `Exercice ${from.slice(0, 4)}` : `Exercice ${from.slice(0, 4)}-${to.slice(0, 4)}`;
}
/** Quand déclarer : dans les 3 mois de la clôture ; au 31 décembre, le 2e jour ouvré après le 1er mai (impots.gouv.fr). */
export function declarationWhen(to: string): string {
  if (to.slice(5) === '12-31') return `début mai ${Number(to.slice(0, 4)) + 1} (le 2e jour ouvré après le 1er mai)`;
  const [y, m] = to.split('-').map(Number);
  const mm = ((m! - 1 + 3) % 12) + 1, yy = y! + (m! + 3 > 12 ? 1 : 0);
  return `avant la fin ${['de janvier', 'de février', 'de mars', 'd’avril', 'de mai', 'de juin', 'de juillet', 'd’août', 'de septembre', 'd’octobre', 'de novembre', 'de décembre'][mm - 1]} ${yy}`;
}

/** Le club dans une phrase, après « pour » : « le Basket Club de Rivebelle », « l’AS Valmorin », « la JS Saint-Ambrel », « Pau Basket ».
 *  La même règle que la base (_rf_the) : scripts/bench/club-article-cases.json passe des deux côtés. */
export function clubThe(name: string): string {
  const v = name.trim().replace(/\s+/g, ' ');
  const first = v.split(' ')[0] ?? '';
  const w = (re: string) => new RegExp(`^(${re})( |$)`, 'i').test(v);
  if (/^(le|la|les) /i.test(v)) return first.toLowerCase() + v.slice(first.length);
  if (/^l['’]/i.test(v)) return `l’${v.slice(2)}`;
  if (w('as|asc|asptt|es|us|union|entente|étoile|etoile|elan|élan|alliance|amicale|association|avenir|espérance|esperance|olympique|éveil|eveil')) return `l’${v}`;
  if (w('js|jeunesse|vaillante|jeanne')) return `la ${v}`;
  if (w('basket|bc|bcm|club|cercle|comité|comite|stade|racing|sporting|foyer|réveil|reveil|cs|sc|ca|co|rc')) return `le ${v}`;
  return v;
}

/** Le texte de la déclaration d'abandon, tel que la base l'écrit et le garde (volunteer_claim_submit). */
export function declarationText(club: string, from: string, to: string, km: number, cents: number): string {
  const M = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const long = (iso: string, year: boolean) => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return `${d === 1 ? '1er' : d} ${M[m! - 1]}${year ? ` ${y}` : ''}`;
  };
  const k = `${String(Math.round(km * 10) / 10).replace('.', ',')} km`;
  return `Je renonce au remboursement des frais de déplacement engagés pour ${clubThe(club)} du ${long(from, from.slice(0, 4) !== to.slice(0, 4))} au ${long(to, true)}`
    + ` (${k}, soit ${eur(cents, ' ')}) et les laisse au club à titre de don.`;
}
